/* Hallmark · pre-emit critique: P5 H5 E5 S5 R4 V5 · macrostructure: Workbench · F3 knobs: columns=4, rule-density=headers-only, numbers=tabular
 * tone: operational-editorial · anchor hue: heritage-green · theme: Operational Architect · nav: N3 side-rail · enrichment: none
 * contrast: pass (40–41) · slop: pass (42–45) · responsive: pass (34, 49–57) · icons: pass (30)
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router';
import {
  Plus,
  ChevronRight,
  Loader2,
  ClipboardCheck,
  Search,
  X,
  Filter,
  Calendar,
  BookOpen,
  RotateCcw,
  RefreshCw,
  ClipboardList,
  CircleCheck,
  Timer,
} from 'lucide-react';
import { useEvaluations } from '../hooks/useEvaluations';
import { usePrograms } from '../hooks/usePrograms';
import { StatusChip } from '@lms/ui';
import { Pagination } from '../components/Pagination';
import type { Evaluation } from '@lms/shared/types';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';

export function Evaluations() {
  const { data: evaluations, isLoading, isFetching, error, refetch } = useEvaluations();
  const { data: programsData } = usePrograms({ limit: 100 });
  const { language, t } = useLanguage();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedType, setSelectedType] = useState<Evaluation['type'] | 'ALL'>('ALL');
  const [selectedProgramId, setSelectedProgramId] = useState<string>('ALL');
  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Click outside listener for suggestions popup
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setIsSuggestOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allEvaluations = useMemo(() => {
    return (evaluations as Evaluation[]) || [];
  }, [evaluations]);

  const programs = useMemo(() => {
    return programsData?.items || [];
  }, [programsData]);

  const evaluationSummary = useMemo(
    () => ({
      total: allEvaluations.length,
      quiz: allEvaluations.filter(item => item.type === 'QUIZ').length,
      exam: allEvaluations.filter(item => item.type === 'EXAM').length,
      survey: allEvaluations.filter(item => item.type === 'SURVEY').length,
    }),
    [allEvaluations]
  );

  const typeFilters = [
    {
      value: 'ALL' as const,
      label: copy.evaluations.filterAllTypes,
      count: evaluationSummary.total,
    },
    {
      value: 'QUIZ' as const,
      label: copy.createEvaluation.optionQuiz,
      count: evaluationSummary.quiz,
    },
    {
      value: 'EXAM' as const,
      label: copy.createEvaluation.optionExam,
      count: evaluationSummary.exam,
    },
    {
      value: 'SURVEY' as const,
      label: copy.createEvaluation.optionSurvey,
      count: evaluationSummary.survey,
    },
  ];

  // Real-time suggestions based on current search input
  const suggestions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return [];
    return allEvaluations
      .filter(item => {
        const titleMatch = item.title?.toLowerCase().includes(query);
        const descMatch = item.description?.toLowerCase().includes(query);
        const programMatch = item.program?.name?.toLowerCase().includes(query);
        return titleMatch || descMatch || programMatch;
      })
      .slice(0, 6);
  }, [allEvaluations, searchQuery]);

  // Filtered evaluations list
  const filteredEvaluations = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    return allEvaluations.filter(item => {
      // 1. Search Query
      if (query) {
        const titleMatch = item.title?.toLowerCase().includes(query);
        const descMatch = item.description?.toLowerCase().includes(query);
        const programMatch = item.program?.name?.toLowerCase().includes(query);
        const materialMatch = item.material?.title?.toLowerCase().includes(query);
        if (!titleMatch && !descMatch && !programMatch && !materialMatch) return false;
      }

      // 2. Type Filter
      if (selectedType !== 'ALL' && item.type !== selectedType) {
        return false;
      }

      // 3. Program Filter
      if (selectedProgramId !== 'ALL') {
        const resolvedProgId =
          item.programId ||
          item.program?.id ||
          item.material?.flow?.programId ||
          item.flow?.programId ||
          item.level?.programId;
        if (resolvedProgId !== selectedProgramId) return false;
      }

      return true;
    });
  }, [allEvaluations, debouncedSearch, selectedType, selectedProgramId]);

  // Pagination calculation
  const totalPages = Math.max(1, Math.ceil(filteredEvaluations.length / pageSize));

  // Current page slice
  const paginatedEvaluations = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredEvaluations.slice(start, start + pageSize);
  }, [filteredEvaluations, currentPage, pageSize]);

  const hasActiveFilters =
    debouncedSearch.trim().length > 0 || selectedType !== 'ALL' || selectedProgramId !== 'ALL';

  const resetFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setSelectedType('ALL');
    setSelectedProgramId('ALL');
    setIsSuggestOpen(false);
    setCurrentPage(1);
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading && allEvaluations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="text-sm font-body text-on-surface-variant">
          <Bilingual {...copy.evaluations.loadingState} />
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-container text-on-error-container p-6 rounded-xl">
        <h3 className="font-headline font-bold mb-2">
          <Bilingual {...copy.evaluations.errorState} />
        </h3>
        <p className="text-sm font-body">{(error as Error).message}</p>
      </div>
    );
  }

  const startRecord = filteredEvaluations.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, filteredEvaluations.length);

  return (
    <div className="space-y-6 sm:space-y-8 animate-fadeIn pb-12">
      <header className="grid gap-6 rounded-3xl bg-surface-container-low p-6 sm:p-8 xl:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)] xl:items-end">
        <div className="min-w-0">
          <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
            <Bilingual {...copy.evaluations.workspaceLabel} />
          </p>
          <h1 className="mt-3 min-w-0 break-words wrap-anywhere font-headline text-3xl font-extrabold tracking-tight text-on-surface sm:text-4xl">
            <Bilingual {...copy.evaluations.title} />
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-on-surface-variant sm:text-base">
            <Bilingual {...copy.evaluations.workspaceDescription} />
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-1">
          <Link
            to="/evaluations/create"
            className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary px-4 text-xs font-label font-bold text-on-primary transition-colors hover:bg-primary-container focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-[0.99]"
          >
            <Plus size={17} />
            <Bilingual {...copy.evaluations.createButton} />
          </Link>
          <Link
            to="/question-bank"
            className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-surface-container-lowest px-4 text-xs font-label font-bold text-on-surface transition-colors hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-[0.99]"
          >
            <BookOpen size={17} className="text-primary" />
            <Bilingual {...copy.evaluations.questionBankButton} />
          </Link>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-surface-container-high px-4 text-xs font-label font-bold text-on-surface transition-colors hover:bg-surface-container-highest focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.99]"
          >
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
            <Bilingual
              {...(isFetching ? copy.evaluations.refreshingButton : copy.evaluations.refreshButton)}
            />
          </button>
        </div>
      </header>

      <section
        aria-label={t(copy.evaluations.workspaceLabel)}
        className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
      >
        {[
          {
            label: copy.evaluations.totalCount,
            value: evaluationSummary.total,
            icon: ClipboardList,
            tone: 'bg-primary text-on-primary',
          },
          {
            label: copy.evaluations.quizCount,
            value: evaluationSummary.quiz,
            icon: CircleCheck,
            tone: 'bg-success-container text-on-success-container',
          },
          {
            label: copy.evaluations.examCount,
            value: evaluationSummary.exam,
            icon: ClipboardCheck,
            tone: 'bg-error-container text-on-error-container',
          },
          {
            label: copy.evaluations.surveyCount,
            value: evaluationSummary.survey,
            icon: Timer,
            tone: 'bg-tertiary-container text-on-tertiary-container',
          },
        ].map(metric => {
          const Icon = metric.icon;
          return (
            <article key={metric.label.id} className={`rounded-2xl p-5 ${metric.tone}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] opacity-75">
                  <Bilingual {...metric.label} />
                </p>
                <Icon size={18} className="shrink-0" />
              </div>
              <p className="mt-5 font-headline text-3xl font-extrabold tracking-tight [font-variant-numeric:tabular-nums]">
                {metric.value}
              </p>
            </article>
          );
        })}
      </section>

      <section
        aria-labelledby="evaluation-discovery-title"
        className="rounded-3xl bg-surface-container-low p-5 sm:p-6"
      >
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2
              id="evaluation-discovery-title"
              className="m-0 font-headline text-xl font-extrabold text-on-surface"
            >
              <Bilingual {...copy.evaluations.searchLabel} />
            </h2>
            <p className="mt-1 text-xs text-on-surface-variant">
              <Bilingual {...copy.evaluations.subtitle} />
            </p>
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-9 items-center justify-center gap-2 self-start whitespace-nowrap rounded-lg bg-error-container px-3 text-xs font-label font-bold text-on-error-container transition-colors hover:bg-error-container/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-error active:scale-[0.99] sm:self-auto"
            >
              <RotateCcw size={14} />
              <Bilingual {...copy.evaluations.filterReset} />
            </button>
          )}
        </div>
        <div className="space-y-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            {/* Search Box with Real-time Auto-Suggest */}
            <div className="relative w-full flex-1 xl:max-w-xl" ref={searchContainerRef}>
              <label
                htmlFor="evaluation-search"
                className="mb-2 block text-[10px] font-label font-bold uppercase tracking-[0.14em] text-on-surface-variant"
              >
                <Bilingual {...copy.evaluations.searchLabel} />
              </label>
              <div className="relative">
                <Search
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant rtl:left-auto rtl:right-4"
                  size={18}
                />
                <input
                  id="evaluation-search"
                  type="text"
                  placeholder={t(copy.evaluations.searchPlaceholder)}
                  className="h-11 w-full rounded-xl bg-surface-container-lowest py-3 pl-11 pr-10 text-sm font-body text-on-surface outline-2 outline-offset-1 outline-transparent transition-colors hover:bg-surface-container-high focus-visible:outline-primary rtl:pl-10 rtl:pr-11"
                  value={searchQuery}
                  onFocus={() => {
                    if (suggestions.length > 0) setIsSuggestOpen(true);
                  }}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    setIsSuggestOpen(true);
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setDebouncedSearch('');
                      setIsSuggestOpen(false);
                      setCurrentPage(1);
                    }}
                    className="absolute right-2 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-lg text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary rtl:right-auto rtl:left-2"
                    aria-label={t(copy.evaluations.clearSearch)}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Auto-Suggest Dropdown */}
              {isSuggestOpen && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-2xl bg-surface-container-lowest p-2 shadow-level4 animate-fadeIn">
                  <div className="flex items-center justify-between px-3 py-2 text-[11px] font-label font-bold text-on-surface-variant">
                    <span>
                      <Bilingual {...copy.evaluations.searchSuggestTitle} />
                    </span>
                    <span className="opacity-60">{suggestions.length}</span>
                  </div>
                  <div className="max-h-72 space-y-1 overflow-y-auto">
                    {suggestions.map(item => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setSearchQuery(item.title);
                          setDebouncedSearch(item.title);
                          setIsSuggestOpen(false);
                          setCurrentPage(1);
                        }}
                        className="group flex w-full items-center justify-between gap-3 rounded-xl p-3 text-left transition-colors hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-[0.99]"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span
                              className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                                item.type === 'EXAM'
                                  ? 'bg-error-container text-on-error-container'
                                  : 'bg-primary/10 text-primary'
                              }`}
                            >
                              {item.type}
                            </span>
                            <p className="font-headline font-bold text-xs text-on-surface group-hover:text-primary transition-colors truncate m-0">
                              {item.title}
                            </p>
                          </div>
                          <p className="text-[11px] font-body text-on-surface-variant opacity-70 truncate m-0">
                            {item.program?.name || item.description || '-'}
                          </p>
                        </div>
                        <div className="shrink-0 flex items-center gap-2 text-on-surface-variant opacity-60">
                          <span className="text-[10px] font-mono font-bold">
                            {item.passingScore}%
                          </span>
                          <ChevronRight size={14} className="rtl:rotate-180" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(15rem,1fr)_minmax(8rem,0.45fr)]">
              <label className="flex min-w-0 flex-col gap-2">
                <span className="inline-flex items-center gap-2 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                  <BookOpen size={13} className="text-secondary" />
                  <Bilingual {...copy.evaluations.filterProgram} />
                </span>
                <select
                  value={selectedProgramId}
                  onChange={e => {
                    setSelectedProgramId(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="h-11 min-w-0 rounded-xl bg-surface-container-lowest px-3 text-sm font-body font-bold text-on-surface outline-2 outline-offset-1 outline-transparent transition-colors hover:bg-surface-container-high focus-visible:outline-primary"
                >
                  <option value="ALL">{t(copy.evaluations.filterAllPrograms)}</option>
                  {programs.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-2">
                <span className="inline-flex items-center gap-2 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                  <Filter size={13} className="text-primary" />
                  <Bilingual {...copy.evaluations.pageSizeLabel} />
                </span>
                <select
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="h-11 rounded-xl bg-surface-container-lowest px-3 text-sm font-body font-bold text-on-surface outline-2 outline-offset-1 outline-transparent transition-colors hover:bg-surface-container-high focus-visible:outline-primary"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>
          </div>

          <div className="flex flex-wrap gap-2" aria-label={t(copy.evaluations.filterType)}>
            {typeFilters.map(filter => (
              <button
                key={filter.value}
                type="button"
                onClick={() => {
                  setSelectedType(filter.value);
                  setCurrentPage(1);
                }}
                aria-pressed={selectedType === filter.value}
                className={`inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-lg px-3 text-xs font-label font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-[0.99] ${
                  selectedType === filter.value
                    ? 'bg-primary text-on-primary'
                    : 'bg-surface-container-lowest text-on-surface hover:bg-surface-container-high'
                }`}
              >
                <Bilingual {...filter.label} />
                <span className="[font-variant-numeric:tabular-nums]">{filter.count}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 px-1 text-xs text-on-surface-variant sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>
              {t(copy.evaluations.resultRange)
                .replace('{start}', String(startRecord))
                .replace('{end}', String(endRecord))
                .replace('{count}', String(filteredEvaluations.length))}
            </span>
            {hasActiveFilters && (
              <span className="text-primary">
                {t(copy.evaluations.resultFiltered).replace(
                  '{total}',
                  String(allEvaluations.length)
                )}
              </span>
            )}
          </div>
          <span className="font-mono text-[11px] [font-variant-numeric:tabular-nums]">
            {t(copy.evaluations.pageIndicator)
              .replace('{page}', String(currentPage))
              .replace('{total}', String(totalPages))}
          </span>
        </div>
      </section>

      <section
        aria-label={t(copy.evaluations.listTitle)}
        className="overflow-hidden rounded-3xl bg-surface-container-low p-3 sm:p-4"
      >
        {/* Table Header (Desktop) */}
        <div className="hidden items-center gap-4 rounded-2xl bg-surface-container-high px-6 py-4 lg:grid lg:grid-cols-[2fr_1fr_1.2fr_0.8fr_1fr_1.1fr_50px]">
          {[
            copy.evaluations.tableHeaderTitle,
            copy.evaluations.tableHeaderCategory,
            copy.evaluations.tableHeaderProgram,
            copy.evaluations.tableHeaderPassingScore,
            copy.evaluations.tableHeaderAttempts,
            copy.evaluations.tableHeaderCreated,
            { id: '', ar: '' },
          ].map((h, idx) => (
            <div
              key={idx}
              className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest"
            >
              <Bilingual {...h} />
            </div>
          ))}
        </div>

        {/* Rows */}
        <div className="mt-2 space-y-2">
          {paginatedEvaluations.map(evaluation => (
            <div
              key={evaluation.id}
              className="group flex flex-col items-start gap-4 rounded-2xl bg-surface-container-lowest p-5 transition-colors hover:bg-surface-container-high lg:grid lg:grid-cols-[2fr_1fr_1.2fr_0.8fr_1fr_1.1fr_50px] lg:items-center lg:gap-4 lg:px-6"
            >
              {/* Node Title & Description */}
              <div className="w-full flex justify-between items-start lg:block min-w-0">
                <div className="min-w-0 pr-2">
                  <div className="text-sm font-body font-bold text-on-surface group-hover:text-primary transition-colors truncate">
                    {evaluation.title}
                  </div>
                  <p className="text-[11px] font-body text-on-surface-variant line-clamp-1 mt-0.5 opacity-60">
                    {evaluation.description || t(copy.evaluations.noDescription)}
                  </p>
                </div>
                <div className="lg:hidden shrink-0">
                  <Link
                    to={`/evaluations/${evaluation.id}`}
                    className="inline-flex size-10 items-center justify-center rounded-lg text-primary transition-colors hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-[0.99]"
                    aria-label={t(copy.evaluations.openEvaluation)}
                  >
                    <ChevronRight size={18} />
                  </Link>
                </div>
              </div>

              {/* Category */}
              <div className="flex items-center justify-between w-full lg:w-auto">
                <span className="lg:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                  <Bilingual {...copy.evaluations.tableHeaderCategory} />
                </span>
                <StatusChip
                  label={
                    evaluation.type === 'EXAM'
                      ? language === 'ar'
                        ? copy.createEvaluation.optionExam.ar
                        : copy.createEvaluation.optionExam.id
                      : evaluation.type === 'QUIZ'
                        ? language === 'ar'
                          ? copy.createEvaluation.optionQuiz.ar
                          : copy.createEvaluation.optionQuiz.id
                        : evaluation.type
                  }
                  variant={
                    evaluation.type === 'EXAM'
                      ? 'error'
                      : evaluation.type === 'QUIZ'
                        ? 'success'
                        : 'neutral'
                  }
                />
              </div>

              {/* Program */}
              <div className="flex items-center justify-between w-full lg:w-auto min-w-0">
                <span className="lg:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                  <Bilingual {...copy.evaluations.tableHeaderProgram} />
                </span>
                <div className="text-xs font-body font-medium text-on-surface truncate">
                  {evaluation.program?.name ||
                    evaluation.material?.flow?.programId ||
                    t(copy.evaluations.noProgram)}
                </div>
              </div>

              {/* Passing Score */}
              <div className="flex items-center justify-between w-full lg:w-auto">
                <span className="lg:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                  <Bilingual {...copy.evaluations.tableHeaderPassingScore} />
                </span>
                <div className="text-sm font-mono font-bold text-on-surface [font-variant-numeric:tabular-nums]">
                  {evaluation.passingScore}%
                </div>
              </div>

              {/* Attempts */}
              <div className="flex items-center justify-between w-full lg:w-auto">
                <span className="lg:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                  <Bilingual {...copy.evaluations.tableHeaderAttempts} />
                </span>
                <div className="text-xs font-body text-on-surface-variant uppercase tracking-wider font-medium">
                  {evaluation.maxAttempts === 0 ? (
                    <Bilingual {...copy.evaluations.attemptInfinite} />
                  ) : language === 'ar' ? (
                    copy.evaluations.attemptMax.ar.replace('{max}', String(evaluation.maxAttempts))
                  ) : (
                    copy.evaluations.attemptMax.id.replace('{max}', String(evaluation.maxAttempts))
                  )}
                </div>
              </div>

              {/* Created Date */}
              <div className="flex items-center justify-between w-full lg:w-auto">
                <span className="lg:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                  <Bilingual {...copy.evaluations.tableHeaderCreated} />
                </span>
                <div className="flex items-center gap-1.5 text-xs font-body text-on-surface-variant/80">
                  <Calendar size={13} className="text-primary/70 shrink-0" />
                  <span>{formatDate(evaluation.createdAt)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="hidden justify-end lg:flex">
                <Link
                  to={`/evaluations/${evaluation.id}`}
                  className="inline-flex size-10 items-center justify-center rounded-lg text-primary transition-colors hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-[0.99]"
                  aria-label={t(copy.evaluations.openEvaluation)}
                >
                  <ChevronRight size={18} />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State when no data or filters return nothing */}
        {filteredEvaluations.length === 0 && (
          <div className="rounded-2xl bg-surface-container-lowest px-6 py-16 text-center">
            <ClipboardCheck className="w-10 h-10 text-on-surface-variant/20 mx-auto mb-2" />
            <p className="text-sm font-body text-on-surface-variant">
              {hasActiveFilters ? (
                <Bilingual {...copy.evaluations.emptyFilteredState} />
              ) : (
                <Bilingual {...copy.evaluations.emptyState} />
              )}
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="mt-4 inline-flex h-10 items-center gap-2 rounded-xl bg-surface-container-high px-4 text-xs font-label font-bold text-primary transition-colors hover:bg-surface-container-highest focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-[0.99]"
              >
                <RotateCcw size={14} />
                <Bilingual {...copy.evaluations.filterReset} />
              </button>
            )}
            {!hasActiveFilters && (
              <Link
                to="/evaluations/create"
                className="mt-4 inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-xl bg-primary px-4 text-xs font-label font-bold text-on-primary transition-colors hover:bg-primary-container focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-[0.99]"
              >
                <Plus size={15} />
                <Bilingual {...copy.evaluations.createButton} />
              </Link>
            )}
          </div>
        )}

        {/* Pagination Controls */}
        {filteredEvaluations.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={newPage => {
              setCurrentPage(newPage);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        )}
      </section>
    </div>
  );
}
